import React, { createContext, useContext, useState } from 'react';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import { useMutation } from '@apollo/client';
import { ANALYTICS_CREATE, ERRORLOG_CREATE } from '../graphql/mutations';
import { Action, Category, Event } from '../libs/analytics';
import { CurrencyDispatchContext, CurrencyStateContext } from './CurrencyContext';
import { LogDispatchContext } from './logContext';
import { AuthContext } from './authContext';

type EnhancedAnalyticsContextType = {
  setAnalyticsUserSession?: any;
  saveAnalytics?: any;
  sessionId?: string;
  saveErrorLog?: any;
};
type EnhancedAnalyticsContextProps = {
  children: React.ReactNode;
};

export const EnhancedAnalyticsContext = createContext<EnhancedAnalyticsContextType>({});

export function EnhancedAnalyticsProvider({ children }: EnhancedAnalyticsContextProps): any {
  const router = useRouter();
  const [updateAnalytics] = useMutation(ANALYTICS_CREATE);
  const [updateErrorLog] = useMutation(ERRORLOG_CREATE);
  const { updateCurrency } = useContext(CurrencyDispatchContext);
  const { log } = useContext(LogDispatchContext);
  const { analyticsValues } = useContext(CurrencyStateContext);
  const { user } = useContext(AuthContext);
  const [sessionId, setSessionId] = useState(uuidv4());

  if (!sessionId) setSessionId(uuidv4());

  function getEvent() {
    let path: any = router.asPath.split('#');
    path = path.length > 1 ? path[1] : path[0];

    const [, conferenceSlug, eventSlug, additionalSlug] = path.split('/');

    if (conferenceSlug === 'login?page=') {
      return `${eventSlug}/${additionalSlug}`;
    }
    return `${conferenceSlug}/${eventSlug}`;
  }

  function getSlug() {
    let path: any = router.asPath.split('#');
    path = path.length > 1 ? path[1] : path[0];

    const [, conferenceSlug, eventSlug, additionalSlug] = path.split('/');

    if (conferenceSlug === 'login?page=') {
      return 'login';
    }
    return path.substring(path.indexOf(additionalSlug));
  }

  async function saveAnalytics(action: Action, category: Category, actionID?: string, userDetails?: any) {
    log.debug({
      source: 'EnhancedAnalyticsContext',
      action: 'SaveAnalytics',
      data: {
        action,
        category,
        actionID,
        userDetails,
        user,
        path: getSlug(),
        event: getEvent(),
        sessionId
      }
    });

    const evt: Event = {
      action,
      category,
      data: {
        id: actionID,
        sessionId,
        path: getSlug(),
        event: getEvent()
      },
      user: userDetails || user
    };

    if (action === Action.CLICK && analyticsValues?.some((item) => item.id === actionID)) updateCurrency(actionID);

    await updateAnalytics({
      variables: {
        input: evt
      }
    }).catch((err) => {
      log.error({
        source: 'EnhancedAnalyticsContext',
        action: 'SaveAnalytics:error',
        error: err
      });
    });
  }

  async function saveErrorLog(component: string, message: string) {
    const evt = {
      data: {
        component,
        message,
        path: getSlug(),
        event: getEvent()
      }
    };

    if (user && user.fName !== '' && user.lName !== '' && user.email !== '') {
      // @ts-ignore
      evt.user = {
        id: user.id,
        fName: user.fName,
        lName: user.lName,
        email: user.email,
        accessLevel: user.accessLevel
      };
    }

    await updateErrorLog({
      variables: {
        input: evt
      }
    }).catch((err) => {
      log.debug({
        source: 'EnhancedAnalyticsContext',
        action: 'SaveError:error',
        error: err
      });
    });
  }

  return (
    <EnhancedAnalyticsContext.Provider
      value={{
        saveAnalytics,
        saveErrorLog,
        sessionId
      }}
    >
      {children}
    </EnhancedAnalyticsContext.Provider>
  );
}

export const withEnhancedAnalyticsContext = (Component: any) => (props: any) => (
  <EnhancedAnalyticsContext.Consumer>
    {(value) => <Component enhancedAnalyticsContext={value} {...props} />}
  </EnhancedAnalyticsContext.Consumer>
);
